if (!customElements.get('mix-match-row')) {
  customElements.define(
    'mix-match-row',
    class MixMatchRow extends HTMLElement {
      constructor() {
        super();

        this.rowIndex = this.dataset.rowIndex;
        this.total = parseInt(this.dataset.total, 10) || 0;

        this.track = this.querySelector('[data-mnm-track]');
        this.prevButton = this.querySelector('[data-mnm-prev]');
        this.nextButton = this.querySelector('[data-mnm-next]');

        this.panel = document.querySelector(
          `[data-mnm-panel="${this.rowIndex}"]`,
        );

        const dataScript = document.querySelector(
          `[data-mnm-row-data="${this.rowIndex}"]`,
        );
        this.products = dataScript ? JSON.parse(dataScript.textContent) : [];

        const initialSlides = Array.from(
          this.querySelectorAll('[data-mnm-slide]'),
        );
        let initialIndex = initialSlides.findIndex((slide) =>
          slide.classList.contains('is-selected'),
        );
        if (initialIndex === -1) {
          initialIndex = Math.min(
            Math.round((this.getVisible() - 1) / 2),
            this.total - 1,
          );
        }

        this.cloneSlides(initialSlides);
        this.loopIndex = this.total + initialIndex;
        this.currentIndex = initialIndex;

        this.prevButton?.addEventListener('click', () => this.step(-1));
        this.nextButton?.addEventListener('click', () => this.step(1));
        this.slides.forEach((slide, index) => {
          slide.addEventListener('click', () => this.goTo(index));
        });

        if (this.panel) {
          const select = this.panel.querySelector('[data-mnm-panel-variant]');
          select?.addEventListener('change', () => this.onVariantChange());
        }

        let resizeTimeout;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => this.render(), 150);
        });

        this.render();
      }

      cloneSlides(initialSlides) {
        if (!this.track || this.total === 0) {
          this.slides = initialSlides;
          return;
        }

        const before = initialSlides.map((slide) => slide.cloneNode(true));
        const after = initialSlides.map((slide) => slide.cloneNode(true));

        before.forEach((slide) => {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('tabindex', '-1');
          this.track.insertBefore(slide, this.track.firstChild);
        });
        after.forEach((slide) => {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('tabindex', '-1');
          this.track.appendChild(slide);
        });

        this.slides = Array.from(this.querySelectorAll('[data-mnm-slide]'));
        this.track.style.setProperty('--mnm-total', this.slides.length);
      }

      getVisible() {
        const value = parseFloat(
          getComputedStyle(this).getPropertyValue('--mnm-visible'),
        );
        return Number.isFinite(value) && value > 0
          ? value
          : parseFloat(this.dataset.visible) || 5;
      }

      step(direction) {
        if (this.total === 0) return;
        this.loopIndex += direction;
        this.currentIndex =
          ((this.loopIndex % this.total) + this.total) % this.total;
        this.render();
        this.recenterAfterTransition();
      }

      goTo(domIndex) {
        if (this.total === 0) return;
        this.loopIndex = domIndex;
        this.currentIndex =
          ((this.loopIndex % this.total) + this.total) % this.total;
        this.render();
        this.recenterAfterTransition();
      }

      recenterAfterTransition() {
        if (!this.track) return;

        const onTransitionEnd = () => {
          this.track.removeEventListener('transitionend', onTransitionEnd);
          if (this.loopIndex < this.total || this.loopIndex >= this.total * 2) {
            this.loopIndex = this.currentIndex + this.total;
            this.track.style.transition = 'none';
            this.render();
            void this.track.offsetHeight;
            this.track.style.transition = '';
          }
        };
        this.track.addEventListener('transitionend', onTransitionEnd);
      }

      render() {
        if (this.total === 0) return;

        const visible = this.getVisible();
        const center = (visible - 1) / 2;
        const offset = this.loopIndex - center;

        if (this.track) {
          this.track.style.setProperty('--mnm-offset', offset);
        }

        this.slides.forEach((slide, index) => {
          slide.classList.toggle(
            'is-selected',
            index % this.total === this.currentIndex,
          );
        });

        this.syncPanel();
      }

      syncPanel() {
        const product = this.products[this.currentIndex];
        if (!product || !this.panel) return;

        const image = this.panel.querySelector('[data-mnm-panel-image]');
        const title = this.panel.querySelector('[data-mnm-panel-title]');
        const price = this.panel.querySelector('[data-mnm-panel-price]');
        const select = this.panel.querySelector('[data-mnm-panel-variant]');
        const input = this.panel.querySelector('[data-mnm-panel-input]');
        const button = this.panel.querySelector('[data-mnm-panel-submit]');
        const links = this.panel.querySelectorAll('[data-mnm-panel-link]');
        const imageLink = this.panel.querySelector('[data-mnm-panel-image-link]');

        if (image && product.image) {
          image.src = product.image;
          image.alt = product.title;
        }
        if (title) title.textContent = product.title;
        if (product.url) {
          links.forEach((link) => {
            link.href = product.url;
          });
        }
        if (imageLink) imageLink.setAttribute('aria-label', product.title);

        if (select) {
          select.innerHTML = '';
          (product.variants || []).forEach((variant) => {
            const option = document.createElement('option');
            option.value = variant.id;
            option.textContent = variant.available
              ? variant.title
              : `${variant.title} - Épuisé`;
            option.disabled = !variant.available;
            select.appendChild(option);
          });

          const firstAvailable =
            (product.variants || []).find((variant) => variant.available) ||
            product.variants?.[0];

          if (firstAvailable) {
            select.value = firstAvailable.id;
            if (price) price.textContent = firstAvailable.formatted_price;
            if (input) input.value = firstAvailable.id;
            if (button)
              button.toggleAttribute('disabled', !firstAvailable.available);
          }
        }
      }

      onVariantChange() {
        const product = this.products[this.currentIndex];
        const select = this.panel.querySelector('[data-mnm-panel-variant]');
        const price = this.panel.querySelector('[data-mnm-panel-price]');
        const input = this.panel.querySelector('[data-mnm-panel-input]');
        const button = this.panel.querySelector('[data-mnm-panel-submit]');
        if (!product || !select) return;

        const variant = (product.variants || []).find(
          (item) => String(item.id) === select.value,
        );
        if (!variant) return;

        if (price) price.textContent = variant.formatted_price;
        if (input) input.value = variant.id;
        if (button) button.toggleAttribute('disabled', !variant.available);
      }
    },
  );
}

document.querySelectorAll('[data-mnm-add-all]').forEach((button) => {
  const root = button.closest('.mnm');
  if (!root) return;

  button.addEventListener('click', async () => {
    const errorEl = root.querySelector('[data-mnm-add-all-error]');
    const labelEl = button.querySelector('[data-mnm-add-all-label]');
    const originalLabel = labelEl ? labelEl.textContent : '';

    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    const items = Array.from(root.querySelectorAll('[data-mnm-panel-input]'))
      .map((input) => parseInt(input.value, 10))
      .filter((id) => Number.isFinite(id) && id > 0)
      .map((id) => ({ id, quantity: 1 }));

    if (!items.length) return;

    button.disabled = true;
    if (labelEl) labelEl.textContent = 'Ajout en cours...';

    try {
      const response = await fetch(button.dataset.mnmAddAllUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'add_failed');
      }

      // même mécanisme que refreshCartDrawer() dans cart-drawer.js : renderContents ouvre le tiroir
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        const sectionIds = cartDrawer
          .getSectionsToRender()
          .map((section) => section.id);
        const sectionsResponse = await fetch(
          `${window.Shopify.routes.root}?sections=${sectionIds.join(',')}`,
        );
        cartDrawer.renderContents({ sections: await sectionsResponse.json() });
      } else {
        window.location.href = button.dataset.mnmCartUrl;
      }

      button.disabled = false;
      if (labelEl) labelEl.textContent = originalLabel;
    } catch (error) {
      button.disabled = false;
      if (labelEl) labelEl.textContent = originalLabel;
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = "Impossible d'ajouter tous les articles (stock insuffisant).";
      }
    }
  });
});

